import React from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet default icon (D1 / Vite safe)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// 🔥 Generic type
export type MapItem = {
  id: string | number;
  title: string;
  lat: number;
  lng: number;
  image?: string;
  description?: string;
  extra?: Record<string, any>; // flexible data
  label?: string; // for custom marker icons
};

type Props = {
  items: MapItem[];
  height?: string;
  zoom?: number;
  center?: [number, number];
  renderPopup?: (item: MapItem) => React.ReactNode;
};

const MapView: React.FC<Props> = ({
  items,
  height = "500px",
  zoom = 10,
  center,
  renderPopup,
}) => {
  // fallback center (first item or Maldives)
  const mapCenter: [number, number] =
    center ||
    (items.length > 0
      ? [items[0].lat, items[0].lng]
      : [3.2028, 73.2207]);

  return (
    <div style={{ height }} className="w-full rounded-xl overflow-hidden">
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        scrollWheelZoom
        className="w-full h-full"
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {items.map((item) => {
          const label = item?.label || item.title || "📍";

          return (
            <Marker
              key={item.id}
              position={[item.lat, item.lng]}
              icon={createMapItemIcon(label)}
            >
              <Popup>
                {renderPopup ? renderPopup(item) : <DefaultPopup item={item} />}
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default MapView;

// 🔹 Default popup UI
const DefaultPopup = ({ item }: { item: MapItem }) => (
  <div className="flex flex-col items-center space-y-2 text-center">
    {item.image && (
      <img
        src={item.image}
        alt={item.title}
        className="w-fit h-16 rounded-full object-cover border-2 border-gray-300"
      />
    )}
    <h3 className="font-semibold">{item.title}</h3>
    {item.description && (
      <p className="text-sm text-gray-500">{item.description}</p>
    )}
  </div>
);

const createPriceIcon = (price: string) =>
  L.divIcon({
    className: "custom-price-marker",
    html: `
      <div class="price-marker">
        ${price}
      </div>
      <div class="marker-pin"></div>
    `,
    iconSize: [80, 40],
    iconAnchor: [40, 40], // bottom center
  });

const createMapItemIcon = (item: string) =>
  L.divIcon({
    className: "custom-price-marker",
    html: `
      <div class="price-marker">
        ${item}
      </div>
      <div class="marker-pin"></div>
    `,
    iconSize: [90, 40],
    iconAnchor: [45, 40], // bottom center
  });